import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PageWrapper from '../components/UI/PageWrapper';
import DashboardGrid from '../components/Dashboard/DashboardGrid';
import DashboardHeader from '../components/Dashboard/DashboardHeader';
import OverviewSection from '../components/Dashboard/OverviewSection';
import MobileDashboard from '../components/Dashboard/Mobile/MobileDashboard';
import ReportCustomizationDropdown from '../components/Dashboard/ReportCustomizationDropdown';
import DateRangePicker from '../components/Dashboard/DateRangePicker';
import { DashboardTile, Report } from '../types/dashboard';
import { dashboardService, PlatformMapping } from '../services/api/dashboard.service';
import { DEFAULT_ARTIST_ID } from '../config/nova-constants';
import { artistAnalyticsService } from '../services/api/artist-analytics.service';
import { useUser } from '../contexts/UserContext';
import { useArtist } from '../contexts/ArtistContext';
import { trackEvent, DASHBOARD_EVENTS } from '../services/analytics';

const DashboardPage = () => {
  const { userProfile } = useUser();
  const { currentArtistId, hasAnyPermission, loadingPermissions } = useArtist();
  const [tiles, setTiles] = useState<DashboardTile[]>([]);
  const [tempTiles, setTempTiles] = useState<DashboardTile[]>([]); // Temporary tiles during customization
  const [isCustomizeMode, setIsCustomizeMode] = useState(false);
  const isUpdatingRef = useRef(false); // Prevent infinite loops during programmatic updates
  const [dateRange, setDateRange] = useState({ from: new Date(), to: new Date() });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<Record<string, number>>({});
  const [platformMapping, setPlatformMapping] = useState<PlatformMapping | null>(null);
  const [platformMappingLoading, setPlatformMappingLoading] = useState(true);
  const [platformMappingError, setPlatformMappingError] = useState<string | null>(null);
  const [userPrimaryArtistId, setUserPrimaryArtistId] = useState<string>(DEFAULT_ARTIST_ID);
  const [availableReports, setAvailableReports] = useState<Report[]>([]);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Uniform dimensions for all charts
  const UNIFORM_CHART_WIDTH = 6;
  const UNIFORM_CHART_HEIGHT = 8;
  const GRID_COLUMNS = 12; // Total columns in the grid
  const CHARTS_PER_ROW = GRID_COLUMNS / UNIFORM_CHART_WIDTH; // 2 charts per row

  // Compact tiles to fill empty spaces while preserving the original order
  const compactTiles = (tiles: DashboardTile[]): DashboardTile[] => {
    if (tiles.length === 0) return tiles;
    
    // Preserve the original order from the array (API response order)
    // Sort by current y position first, then x, to detect gaps
    const sortedByPosition = [...tiles].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });
    
    // Check if there are gaps by comparing expected vs actual positions
    let hasGaps = false;
    sortedByPosition.forEach((tile, index) => {
      const expectedRow = Math.floor(index / CHARTS_PER_ROW);
      const expectedCol = index % CHARTS_PER_ROW;
      const expectedX = expectedCol * UNIFORM_CHART_WIDTH;
      const expectedY = expectedRow * UNIFORM_CHART_HEIGHT;
      
      if (tile.x !== expectedX || tile.y !== expectedY) {
        hasGaps = true;
      }
    });
    
    // If there are gaps, recalculate positions to fill them while preserving order
    if (hasGaps) {
      // Use original array order, but recalculate positions to fill gaps
      return tiles.map((tile, index) => {
        const row = Math.floor(index / CHARTS_PER_ROW);
        const col = index % CHARTS_PER_ROW;
        
        return {
          ...tile,
          x: col * UNIFORM_CHART_WIDTH,
          y: row * UNIFORM_CHART_HEIGHT,
          w: UNIFORM_CHART_WIDTH,
          h: UNIFORM_CHART_HEIGHT,
          minW: UNIFORM_CHART_WIDTH,
          minH: UNIFORM_CHART_HEIGHT,
          maxW: UNIFORM_CHART_WIDTH,
          maxH: UNIFORM_CHART_HEIGHT
        };
      });
    }
    
    // No gaps detected, just normalize dimensions while preserving positions
    return tiles.map(tile => ({
      ...tile,
      w: UNIFORM_CHART_WIDTH,
      h: UNIFORM_CHART_HEIGHT,
      minW: UNIFORM_CHART_WIDTH,
      minH: UNIFORM_CHART_HEIGHT,
      maxW: UNIFORM_CHART_WIDTH,
      maxH: UNIFORM_CHART_HEIGHT
    }));
  };

  // Normalize tile dimensions to uniform size, preserve order, and fill gaps if any
  const normalizeTileDimensions = (tiles: DashboardTile[]): DashboardTile[] => {
    // Preserve order from API, but compact to fill gaps
    return compactTiles(tiles);
  };

  // Get default tiles from the service to ensure consistency
  const defaultTiles: DashboardTile[] = [
    // Analytics Section - Monthly Listeners and Followers side by side (50% each)
    { id: 'monthly-listeners', x: 0, y: 0, w: 6, h: 8, title: 'Revenue & Streaming Performance', type: 'chart', isExpanded: false, minW: 6, minH: 6, maxW: 12, maxH: 25 },
    { id: 'monthly-followers', x: 0, y: 0, w: 12, h: 4, title: 'Monthly Followers', type: 'chart', isExpanded: false, minW: 6, minH: 6, maxW: 12, maxH: 4 },
    
    // Small charts (4 cols) - row 2: Brand Affinity, Editorial Playlists (YouTube Views removed)
    { id: 'brand-affinity', x: 0, y: 8, w: 6, h: 4, title: 'Brand Affinity', type: 'chart', isExpanded: false, minW: 3, minH: 3, maxW: 6, maxH: 6 },
    { id: 'editorial-playlists', x: 6, y: 8, w: 6, h: 4, title: 'Editorial Playlist Reach', type: 'chart', isExpanded: false, minW: 4, minH: 3, maxW: 6, maxH: 6 },
    
    // Medium charts (6 cols) - 2 per row
    { id: 'geography', x: 0, y: 12, w: 6, h: 5, title: 'Geographic Distribution', type: 'chart', isExpanded: false, minW: 6, minH: 4, maxW: 12, maxH: 8 },
    { id: 'platforms', x: 6, y: 12, w: 6, h: 5, title: 'Total Playlist Count', type: 'chart', isExpanded: false, minW: 6, minH: 4, maxW: 12, maxH: 8 },
    
    // Growth charts (4 cols each) - row 4: Weekly Growth, Monthly Growth, Platform Growth Comparison
    { id: 'weekly-growth', x: 0, y: 17, w: 4, h: 4, title: 'Weekly Growth', type: 'chart', isExpanded: false, minW: 4, minH: 3, maxW: 6, maxH: 6 },
    { id: 'monthly-growth', x: 4, y: 17, w: 4, h: 4, title: 'Monthly Growth', type: 'chart', isExpanded: false, minW: 4, minH: 3, maxW: 6, maxH: 6 },
    { id: 'platform-growth-comparison', x: 8, y: 17, w: 4, h: 4, title: 'Platform Growth Comparison', type: 'chart', isExpanded: false, minW: 4, minH: 3, maxW: 6, maxH: 6 },
    
  // Spotify Funnel Chart - positioned at the end
  { id: 'spotify-funnel', x: 0, y: 21, w: 6, h: 6, title: 'Spotify Engagement Funnel', type: 'chart', isExpanded: false, minW: 6, minH: 5, maxW: 12, maxH: 10 },
  { id: 'engagement-rates', x: 6, y: 21, w: 6, h: 6, title: 'Engagement Rates', type: 'chart', isExpanded: false, minW: 6, minH: 4, maxW: 12, maxH: 10 },
  
  // Audience Reach Pyramid - hidden for now
  // { id: 'downloads', x: 8, y: 21, w: 4, h: 4, title: 'Audience Reach Pyramid', type: 'metric', isExpanded: false, minW: 4, minH: 3, maxW: 6, maxH: 6 },
  ];

  // Load dashboard data when artist changes
  useEffect(() => {
    const loadUserLayout = async () => {
      // Don't load if no artist is selected
      if (!currentArtistId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Use currentArtistId from context (convert to string for API)
        const artistId = currentArtistId.toString();
        setUserPrimaryArtistId(artistId);
        
        // Step 1: Load platform mapping for secondary DB charts using the correct artist ID
        try {
          setPlatformMappingLoading(true);
          setPlatformMappingError(null);
          
          const mapping = await dashboardService.getPlatformMapping(artistId);
          
          if (mapping) {
            setPlatformMapping(mapping);
            setPlatformMappingError(null);
          } else {
            setPlatformMapping(null);
            setPlatformMappingError('No platform mapping found for this artist');
          }
        } catch (mappingError: any) {
          setPlatformMapping(null);
          setPlatformMappingError(mappingError.message || 'Failed to load platform mapping');
        } finally {
          setPlatformMappingLoading(false);
        }
        
        // Step 2: Load user layout (this is artist-agnostic, but we still need it)
        const userLayout = await dashboardService.getUserLayout();

        let finalTiles: DashboardTile[] = [];
        if (userLayout && userLayout.length > 0) {
          // Use ONLY the tiles returned from backend (already filtered by is_visible)
          // Backend returns full tile data with w, h, so no need to merge with defaultTiles
          finalTiles = userLayout;
          // Normalize dimensions to uniform size for consistency
          finalTiles = normalizeTileDimensions(finalTiles);
          setTiles(finalTiles);
          setTempTiles(finalTiles);
        } else {
          // First-time user: Use default visible tiles
        const defaultVisibleTiles = defaultTiles.filter(tile =>
          ['monthly-listeners', 'monthly-followers', 'editorial-playlists',
           'platforms', 'brand-affinity', 'platform-growth-comparison', 'engagement-rates',
           'instagram-gender-distribution', 'instagram-age-distribution', 'youtube-gender-distribution'].includes(tile.id)
        );
        finalTiles = defaultVisibleTiles;
        // Normalize dimensions to uniform size for consistency
        finalTiles = normalizeTileDimensions(finalTiles);
        setTiles(finalTiles);
        setTempTiles(finalTiles);
        }

        // Step 3: Force refresh all tiles to reload data for new artist
        setRefreshKey(prev => {
          const newKeys: Record<string, number> = {};
          finalTiles.forEach(tile => {
            newKeys[tile.id] = (prev[tile.id] || 0) + 1;
          });
          return newKeys;
        });
      } catch (_err) {
        setError('Failed to load dashboard layout');
        // Fallback to default tiles on error
        const normalizedDefaultTiles = normalizeTileDimensions(defaultTiles);
        setTiles(normalizedDefaultTiles);
        setTempTiles(normalizedDefaultTiles);
      } finally {
        setLoading(false);
      }
    };

    // Initialize default date range only on first load
    if (dateRange.from.getTime() === dateRange.to.getTime()) {
      const today = new Date();
      const sixMonthsAgo = new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000);
      setDateRange({ from: sixMonthsAgo, to: today });
    }

    loadUserLayout();
  }, [currentArtistId]); // Refetch when artist changes

  // Load available reports for customization dropdown - wait for artist to be selected
  useEffect(() => {
    // Don't load if no artist is selected
    if (!currentArtistId) {
      return;
    }

    const loadAvailableReports = async () => {
      try {
        console.log('[DashboardPage] Loading available reports with artistId:', currentArtistId);
        const reports = await dashboardService.getAvailableReports();
            setAvailableReports(reports);
      } catch (_error) {
        console.error('[DashboardPage] Error loading available reports:', _error);
        setAvailableReports([]);
      }
    };

    loadAvailableReports();
  }, [currentArtistId]); // Wait for artist to be selected


  const handleLayoutChange = (newTiles: DashboardTile[]) => {
    // Ignore layout changes triggered by programmatic updates (add/remove tile)
    if (isUpdatingRef.current) {
      return;
    }

    // Never auto-save layout changes - only save when user explicitly clicks "Save" in customize mode
    // This prevents saving during window resize or other automatic layout adjustments
    if (isCustomizeMode) {
      // In customize mode, only update temp tiles for preview (user is dragging/resizing)
      setTempTiles(newTiles);
    }
    // Outside customize mode: DO NOTHING to prevent infinite loop
    // The tiles state is already correct and should not be updated on every layout change
  };


  const handleCustomizeToggle = async () => {
    if (isCustomizeMode) {
      // User clicked "Done" - save the temporary layout positions
      setSaving(true);

      // Track layout save
      trackEvent(DASHBOARD_EVENTS.LAYOUT_SAVED);

      try {
        // Normalize dimensions but preserve user's dragged positions
        const normalizedTiles = tempTiles.map(tile => ({
          ...tile,
          w: UNIFORM_CHART_WIDTH,
          h: UNIFORM_CHART_HEIGHT,
          minW: UNIFORM_CHART_WIDTH,
          minH: UNIFORM_CHART_HEIGHT,
          maxW: UNIFORM_CHART_WIDTH,
          maxH: UNIFORM_CHART_HEIGHT
        }));
        const success = await dashboardService.saveUserLayout(normalizedTiles);
        if (success) {
          setTiles(normalizedTiles);
        } else {
          setError('Failed to save dashboard layout');
          // Revert temp tiles to current tiles
          setTempTiles(tiles);
        }
      } catch (_err) {
        setError('Error saving dashboard layout');
        // Revert temp tiles to current tiles
        setTempTiles(tiles);
      } finally {
        setSaving(false);
      }
    } else {
      // User clicked "Customize" - initialize temp tiles
      setTempTiles([...tiles]);
    }
    setIsCustomizeMode(!isCustomizeMode);
  };

  const handleTileToggle = (tileId: string) => {
    // This function is no longer used since expand is now handled by modal
  };

  const handleDateRangeChange = (from: Date, to: Date) => {
    setDateRange({ from, to });

    // Track date range change
    const diffDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
    let rangeType = 'custom';
    if (diffDays <= 7) rangeType = 'week';
    else if (diffDays <= 30) rangeType = 'month';
    else if (diffDays <= 90) rangeType = 'quarter';
    else if (diffDays <= 180) rangeType = '6_months';
    else if (diffDays <= 365) rangeType = 'year';

    trackEvent(DASHBOARD_EVENTS.DATE_RANGE_CHANGED, { range_type: rangeType });
  };

  const handleTileRefresh = (tileId: string) => {
    // Increment the refresh key for this specific tile to force a re-render
    setRefreshKey(prev => ({
      ...prev,
      [tileId]: (prev[tileId] || 0) + 1
    }));
  };

  // Handler: Toggle report visibility (Add/Remove) with optimistic updates
  const handleToggleReport = async (reportId: string, isVisible: boolean) => {
    // Track report toggle
    trackEvent(DASHBOARD_EVENTS.REPORT_TOGGLED, {
      tile_type: reportId,
      action: isVisible ? 'add' : 'remove',
    });

    // Store original state for rollback
    const originalTiles = [...tiles];
    const originalTempTiles = [...tempTiles];
    const originalAvailableReports = [...availableReports];

    try {
      setError(null);

      // OPTIMISTIC UPDATE - Update UI immediately
      // Update available reports state optimistically
      setAvailableReports(prev =>
        prev.map(r => r.id === reportId ? { ...r, isVisible } : r)
      );

      // Set flag to prevent handleLayoutChange from triggering during programmatic updates
      isUpdatingRef.current = true;

      if (isVisible) {
        // Adding a tile - need to get its default configuration
        const defaultTile = defaultTiles.find(t => t.id === reportId);
        if (defaultTile) {
          // Normalize the new tile to uniform dimensions
          const normalizedTile = normalizeTileDimensions([defaultTile])[0];
          // In customize mode: only update tempTiles
          // Outside customize mode: update both tiles and tempTiles
          if (isCustomizeMode) {
            const newTempTiles = [...tempTiles, normalizedTile];
            const compactedTiles = compactTiles(newTempTiles);
            setTempTiles(compactedTiles);
          } else {
            const newTiles = [...tiles, normalizedTile];
            const compactedTiles = compactTiles(newTiles);
            setTiles(compactedTiles);
            setTempTiles(compactedTiles);
          }
        }
      } else {
        // Removing a tile - filter it out and compact remaining tiles
        // In customize mode: only update tempTiles
        // Outside customize mode: update both tiles and tempTiles
        if (isCustomizeMode) {
          const filteredTiles = tempTiles.filter(t => t.id !== reportId);
          const compactedTiles = compactTiles(filteredTiles);
          setTempTiles(compactedTiles);
        } else {
          const filteredTiles = tiles.filter(t => t.id !== reportId);
          const compactedTiles = compactTiles(filteredTiles);
          setTiles(compactedTiles);
          setTempTiles(compactedTiles);
        }
      }

      // Reset flag after a short delay to allow react-grid-layout to settle
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);

      // Now call backend API to persist the change
      await dashboardService.toggleTileVisibility(reportId, isVisible);

      // Small delay to ensure database transaction is fully committed
      await new Promise(resolve => setTimeout(resolve, 100));

      // Reload from backend to ensure we're in sync
      const userLayout = await dashboardService.getUserLayout();
      // Normalize dimensions to uniform size while preserving x, y positions
      const normalizedLayout = normalizeTileDimensions(userLayout);

      // Set flag to prevent handleLayoutChange during backend reload
      isUpdatingRef.current = true;

      // In customize mode: only update tempTiles
      // Outside customize mode: update both tiles and tempTiles
      if (isCustomizeMode) {
        setTempTiles(normalizedLayout);
      } else {
        setTiles(normalizedLayout);
        setTempTiles(normalizedLayout);
      }

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);

      // CRITICAL: Reload availableReports from backend to get correct visibility status
      // Don't derive from tiles - fetch from the source of truth
      const updatedReports = await dashboardService.getAvailableReports();
      setAvailableReports(updatedReports);

    } catch (_error) {

      // ROLLBACK - Restore original state
      // Reset the ref flag first
      isUpdatingRef.current = true;

      // In customize mode: only rollback tempTiles
      // Outside customize mode: rollback both tiles and tempTiles
      if (isCustomizeMode) {
        setTempTiles(originalTempTiles);
      } else {
        setTiles(originalTiles);
        setTempTiles(originalTempTiles);
      }

      setAvailableReports(originalAvailableReports);

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);

      setError('Failed to update report visibility. Please try again.');
    }
  };

  // Handler: Remove tile from dashboard (via tile menu)
  const handleRemoveTile = async (tileId: string) => {
    await handleToggleReport(tileId, false);
  };

  const handleResetLayout = async () => {
    try {
      setLoading(true);
      setError(null);

      // Reset layout on backend so all overrides are cleared
      await dashboardService.resetUserLayout();

      // After reset, load fresh layout + reports from the backend (will fall back to defaults)
      const userLayout = await dashboardService.getUserLayout();
      // Normalize dimensions to uniform size while preserving x, y positions
      const normalizedLayout = normalizeTileDimensions(userLayout);

      isUpdatingRef.current = true;
      setTiles(normalizedLayout);
      setTempTiles(normalizedLayout);
      setIsCustomizeMode(false);

      try {
        const reports = await dashboardService.getAvailableReports();
        setAvailableReports(reports);
      } catch (_reportsError) {
        setAvailableReports([]);
      }
    } catch (_err) {
      setError('Failed to reset dashboard layout');

      // Fallback to local default visible tiles
      const defaultVisibleTiles = defaultTiles.filter(tile =>
        ['monthly-listeners', 'monthly-followers', 'editorial-playlists',
         'platforms', 'brand-affinity', 'platform-growth-comparison', 'engagement-rates',
         'instagram-gender-distribution', 'instagram-age-distribution', 'youtube-gender-distribution'].includes(tile.id)
      );
      // Normalize dimensions to uniform size while preserving x, y positions
      const normalizedDefaultTiles = normalizeTileDimensions(defaultVisibleTiles);
      setTiles(normalizedDefaultTiles);
      setTempTiles(normalizedDefaultTiles);
      setIsCustomizeMode(false);
    } finally {
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="mx-auto p-12 text-black rounded-lg profile-form flex flex-col gap-[2.875rem]">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-400">Loading dashboard...</div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="mx-auto p-12 text-black rounded-lg profile-form flex flex-col gap-[2.875rem] font-family-messina-sans-regular">
        <DashboardHeader
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />
        
        {error && (
          <div className="bg-red-900 border border-red-600 text-red-200 px-4 py-3 rounded-lg">
            <p className="text-sm">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 text-sm underline ml-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Show Overview + Analytics only when artist is mapped (old logic) */}
        {userProfile?.artist?.[0]?.is_artist_mapped === true && (
          <div className="border border-[#BBBBBB] rounded-lg p-6" style={{ borderWidth: '1px' }}>
          <>
            {isMobile ? (
              <MobileDashboard
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                reports={availableReports}
                onToggleReport={handleToggleReport}
                platformArtistId={platformMapping?.platformArtistId}
                platformMappingLoading={platformMappingLoading}
                platformMappingError={platformMappingError}
                userPrimaryArtistId={userPrimaryArtistId}
                analyticsActionsEnabled={
                  !loadingPermissions &&
                  hasAnyPermission([
                    'analytics:view',
                    'analytics:manage',
                    'analytics:customize',
                  ])
                }
              />
            ) : (
              <>
                <OverviewSection dateRange={dateRange} />

                {/* Analytics section: only when artist is mapped */}
                <>
                  {loadingPermissions ? (
                    <div className="mb-6 text-center py-8">
                      <div className="text-gray-500 font-family-messina-sans">Loading permissions...</div>
                    </div>
                  ) : hasAnyPermission(["analytics:view", "analytics:manage", "analytics:customize"]) ? (
                    <>
                      <div className="mb-6 flex items-center justify-between">
                        <h1 className="text-2xl font-bold text-black">Analytics</h1>
                        <div className="flex items-center gap-4">
                          <DateRangePicker
                            dateRange={dateRange}
                            onDateRangeChange={handleDateRangeChange}
                          />
                          <ReportCustomizationDropdown
                            reports={availableReports}
                            onToggleReport={handleToggleReport}
                          />
                          <button
                            onClick={handleCustomizeToggle}
                            disabled={saving}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                              saving ? 'bg-gray-600 cursor-not-allowed' : 'bg-gray-800 hover:bg-gray-700'
                            } text-white`}
                          >
                            <span className="text-sm-nova">
                              {saving ? 'Saving...' : isCustomizeMode ? 'Save' : 'Customize'}
                            </span>
                          </button>
                          {isCustomizeMode && (
                            <button
                              onClick={handleResetLayout}
                              className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                              <span className="text-sm-nova">Set Default View</span>
                            </button>
                          )}
                        </div>
                      </div>
                      <DashboardGrid
                        tiles={isCustomizeMode ? tempTiles : tiles}
                        onLayoutChange={handleLayoutChange}
                        onTileToggle={handleTileToggle}
                        isCustomizeMode={isCustomizeMode}
                        dateRange={dateRange}
                        onTileRefresh={handleTileRefresh}
                        refreshKeys={refreshKey}
                        platformMapping={platformMapping}
                        platformMappingLoading={platformMappingLoading}
                        platformMappingError={platformMappingError}
                        userPrimaryArtistId={userPrimaryArtistId}
                        onRemoveTile={handleRemoveTile}
                      />
                    </>
                  ) : (
                    <div className="mb-6 text-center py-8">
                      <div className="text-gray-500 font-family-messina-sans">
                        You don't have permission to view analytics for this artist.
                      </div>
                    </div>
                  )}
                </>
              </>
            )}
          </>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default DashboardPage;
